export default function EaganTravelTimingSection() {
  return (
    <section className="bg-[#F7F8FA] py-20 md:py-28">
      <div className="max-w-7xl mx-auto px-4 md:px-8">
        <div className="text-center mb-14">
          <p className="text-[#F6C344] text-xs font-bold uppercase tracking-[0.2em] mb-3">Regional Travel Expertise</p>
          <h2 className="font-serif text-3xl md:text-4xl font-bold text-[#1A365D] mb-4">
            Travel &amp; Passport Timing for Eagan Residents
          </h2>
          <p className="text-gray-500 max-w-2xl mx-auto text-base leading-relaxed">
            Understanding how Eagan travelers actually move helps us anticipate the passport urgencies that arise here. From corporate corridor sprints to family cruise coordination, south metro travel operates on a unique rhythm.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-5 mb-12">
          {/* MSP Airport */}
          <div className="bg-white rounded-lg p-7 border border-gray-200">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 flex items-center justify-center rounded-full bg-[#1A365D]/5 flex-shrink-0">
                <i className="ri-flight-takeoff-line text-[#1A365D] text-lg"></i>
              </div>
              <div>
                <h3 className="font-bold text-[#1A365D] text-sm">MSP — Minneapolis-Saint Paul International Airport</h3>
                <p className="text-gray-400 text-xs">About 5 miles from Eagan — 10 minutes by car</p>
              </div>
            </div>
            <p className="text-gray-500 text-sm leading-relaxed mb-4">
              Eagan is practically in MSP Airport&apos;s backyard — making it one of the closest suburbs to Minnesota&apos;s primary international gateway. The airport is one of the largest in the Midwest with nonstop service to Amsterdam, London, Paris, Tokyo, and Reykjavik. Eagan&apos;s location just south of the airport means business travelers at the Lone Oak Road and Pilot Knob Road corridors can be at their gate in under 20 minutes. This proximity creates a unique passport urgency dynamic — when a departure is at MSP, a passport problem discovered on Monday has a very short resolution window.
            </p>
            <div className="flex items-start gap-2 bg-[#F6C344]/8 rounded-md p-3">
              <i className="ri-lightbulb-flash-line text-[#F6C344] text-sm flex-shrink-0 mt-0.5"></i>
              <p className="text-gray-600 text-xs leading-relaxed">
                <strong className="text-[#1A365D]">Passport tip:</strong> Because MSP is so close, many Eagan travelers book international flights with minimal lead time. Check your passport expiration before every travel planning conversation, not after tickets are purchased.
              </p>
            </div>
          </div>

          {/* Corporate Travel */}
          <div className="bg-white rounded-lg p-7 border border-gray-200">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 flex items-center justify-center rounded-full bg-[#1A365D]/5 flex-shrink-0">
                <i className="ri-briefcase-4-line text-[#1A365D] text-lg"></i>
              </div>
              <div>
                <h3 className="font-bold text-[#1A365D] text-sm">Corporate Travel</h3>
                <p className="text-gray-400 text-xs">Thomson Reuters, Blue Cross, and Eagan&apos;s corporate corridor</p>
              </div>
            </div>
            <p className="text-gray-500 text-sm leading-relaxed mb-4">
              Eagan&apos;s major employers — Thomson Reuters, Blue Cross Blue Shield of Minnesota, Capella Education, and dozens of technology and financial services companies — send employees abroad regularly. These corporate travel patterns are often calendar-driven: quarterly business reviews, client meetings, and training programs that book with 7–21 days notice. A consultant at Thomson Reuters discovers their passport expired during a pandemic travel lull. An operations director at Blue Cross needs to fly to Dublin. A software engineer gets assigned to Singapore.
            </p>
            <div className="flex items-start gap-2 bg-[#F6C344]/8 rounded-md p-3">
              <i className="ri-lightbulb-flash-line text-[#F6C344] text-sm flex-shrink-0 mt-0.5"></i>
              <p className="text-gray-600 text-xs leading-relaxed">
                <strong className="text-[#1A365D]">Passport tip:</strong> Corporate travelers should set a calendar reminder 9 months before passport expiration. This is early enough to renew by standard mail and avoid the premium expedited fees.
              </p>
            </div>
          </div>

          {/* Family & Leisure Travel */}
          <div className="bg-white rounded-lg p-7 border border-gray-200">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 flex items-center justify-center rounded-full bg-[#1A365D]/5 flex-shrink-0">
                <i className="ri-parent-line text-[#1A365D] text-lg"></i>
              </div>
              <div>
                <h3 className="font-bold text-[#1A365D] text-sm">Family &amp; Leisure Travel</h3>
                <p className="text-gray-400 text-xs">Cedar Grove, Patrick Eagan Park, and community family travel</p>
              </div>
            </div>
            <p className="text-gray-500 text-sm leading-relaxed mb-4">
              Eagan&apos;s strong school district and family-oriented neighborhoods generate significant leisure travel — European tours, Caribbean cruises, and spring break trips. Families with children face a particular challenge: child passports are only valid for 5 years, and parents often lose track of expiration dates between vacations. A family in Cedar Grove plans a European summer vacation and realizes their 12-year-old&apos;s passport expired last year.
            </p>
            <div className="flex items-start gap-2 bg-[#F6C344]/8 rounded-md p-3">
              <i className="ri-lightbulb-flash-line text-[#F6C344] text-sm flex-shrink-0 mt-0.5"></i>
              <p className="text-gray-600 text-xs leading-relaxed">
                <strong className="text-[#1A365D]">Passport tip:</strong> Check every family member&apos;s passport expiration at the start of the school year. Make it a September tradition before spring break bookings begin.
              </p>
            </div>
          </div>

          {/* International Medical & Professional Conferences */}
          <div className="bg-white rounded-lg p-7 border border-gray-200">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 flex items-center justify-center rounded-full bg-[#1A365D]/5 flex-shrink-0">
                <i className="ri-hospital-line text-[#1A365D] text-lg"></i>
              </div>
              <div>
                <h3 className="font-bold text-[#1A365D] text-sm">International Medical &amp; Professional Conferences</h3>
                <p className="text-gray-400 text-xs">Healthcare and insurance industry travel from Eagan</p>
              </div>
            </div>
            <p className="text-gray-500 text-sm leading-relaxed mb-4">
              Blue Cross Blue Shield and related healthcare organizations in Eagan regularly send professionals to international conferences, compliance training, and industry summits in Europe and Asia. These trips are typically on the organizational calendar but individual travelers may receive notice 3–4 weeks before departure. A compliance officer needs to attend a conference in Vienna. A data analyst gets invited to a healthcare technology summit in Singapore.
            </p>
            <div className="flex items-start gap-2 bg-[#F6C344]/8 rounded-md p-3">
              <i className="ri-lightbulb-flash-line text-[#F6C344] text-sm flex-shrink-0 mt-0.5"></i>
              <p className="text-gray-600 text-xs leading-relaxed">
                <strong className="text-[#1A365D]">Passport tip:</strong> Healthcare professionals traveling internationally for conferences should maintain passport validity with at least 12 months remaining. Many conference destinations have strict entry requirements.
              </p>
            </div>
          </div>
        </div>

        <div className="bg-[#1A365D] rounded-lg p-7 flex flex-col md:flex-row items-center gap-6">
          <div className="w-14 h-14 flex items-center justify-center rounded-full bg-[#F6C344]/15 flex-shrink-0">
            <i className="ri-building-line text-[#F6C344] text-2xl"></i>
          </div>
          <div className="flex-1 text-center md:text-left">
            <h3 className="font-bold text-white text-base mb-2">Corporate Event &amp; Conference Travel</h3>
            <p className="text-white/60 text-sm leading-relaxed">
              Eagan&apos;s corporate campuses host major industry conferences and events that draw international attendees. Event organizers, keynote speakers, and corporate attendees flying in from abroad — or Eagan professionals traveling to partner events overseas — sometimes discover passport issues during event planning. We accommodate urgent corporate timelines with same-day document review and prioritized submission.
            </p>
          </div>
          <a
            href="https://fastpassportcenter.com/checkout/"
            className="bg-[#F6C344] hover:bg-[#e5b430] text-[#1A365D] font-bold text-sm px-6 py-3 rounded-md transition-all whitespace-nowrap flex-shrink-0"
          >
            Get Started <i className="ri-arrow-right-line ml-1"></i>
          </a>
        </div>
      </div>
    </section>
  );
}